import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Phone, Clock, Star } from 'lucide-react';
import CoverageMap from '../components/CoverageMap';

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    els.forEach((el) => el.classList.remove('visible'));
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
    );
    const timer = setTimeout(() => els.forEach((el) => observer.observe(el)), 50);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);
}

const features = [
  { title: 'Genuine Medicines Only', desc: 'We source directly from licensed distributors — zero counterfeit products, guaranteed.', icon: '🛡️' },
  { title: 'Expert Pharmacists', desc: 'Each outlet is staffed with qualified, registered pharmacists for professional guidance.', icon: '👨‍⚕️' },
  { title: 'Digital Billing', desc: 'Cashless payments, digital receipts, and GST-compliant billing at every outlet.', icon: '📱' },
  { title: 'Wide Range', desc: '5,000+ products including branded, generic, OTC, wellness, and FMHG products — and growing.', icon: '💊' },
  { title: 'Affordable Pricing', desc: 'Competitive MRP pricing with special discounts on generic medicines.', icon: '💰' },
  { title: 'Loyalty Program', desc: 'Earn APlus Health Points on every purchase and redeem for discounts.', icon: '🏆' },
];

const outlets = [
  { city: 'Salem', area: 'Arisipalayam', address: '133, Thammannan Road, Arisipalayam, Salem – 636009, Tamil Nadu', phone: '+91 9677779741', hours: '9 AM - 9 PM', status: 'Open' },
];

const businessModelSteps = [
  {
    step: "01",
    title: "Retail Pharmacy Outlets",
    desc: "Company-owned and franchise pharmacy stores under APlus Pharmacy brand"
  },
  {
    step: "02",
    title: "Generic Medicine Focus",
    desc: "Championing affordable generic medicines to reduce healthcare costs for all"
  },
  {
    step: "03",
    title: "Digital Operations",
    desc: "Technology-enabled billing, inventory, and patient management systems"
  },
  {
    step: "04",
    title: "Franchise Growth",
    desc: "Rapid expansion through a proven franchise model across India"
  },
];

const Pharmacy = () => {
  useScrollReveal();
  return (
    <div className="">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-900 text-white py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-10 w-72 h-72 bg-secondary-400/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-semibold mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
                🏪 India's Next-Generation Pharmacy Retail Chain
                <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight mb-5">
                <span className="text-secondary-400">APlus Pharmacy</span>
                <br />Where Care Meets Convenience
              </h1>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-white/30 rounded-full"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-secondary-400"></span>
                <span className="h-px w-10 bg-white/30 rounded-full"></span>
              </div>
              <p className="text-white/80 text-base lg:text-lg leading-relaxed mb-8">
                APlus Pharmacy is the retail brand of APLUS Healthcare Solutions Pvt. Ltd. Our pharmacy outlets are designed to provide a professional, trustworthy, and patient-friendly experience — offering genuine medicines, expert advice, and complete wellness products.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/franchise" className="btn-secondary">Become a Franchisee <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/contact" className="btn-outline">Find Nearest Outlet</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Outlet Operational', value: '1' },
                { label: 'City', value: 'Salem' },
                { label: 'Customers Served', value: '500+' },
                { label: 'Products Available', value: '5,000+' },
              ].map((stat, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
                  <div className="font-heading text-3xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-white/70 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Makes APlus Different */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
              Our Promise
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">The APlus Pharmacy</span>
              <span className="gradient-text">Experience</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-secondary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">Walk into any APlus Pharmacy and experience the difference — professional, clean, trustworthy, and affordable.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="reveal card">
                <div className="text-4xl mb-4">{f.icon}</div>
                <h3 className="font-heading font-bold text-gray-900 text-lg mb-3">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Business Model */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 rounded-3xl p-8 lg:p-10 text-white">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 rounded-full px-4 py-2 text-sm font-semibold mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-white/70 inline-block"></span>
                How We Operate
                <span className="w-1.5 h-1.5 rounded-full bg-white/70 inline-block"></span>
              </div>
              <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-2">Our Business Model</h2>
              <div className="flex items-center justify-center gap-3 mt-3">
                <span className="h-px w-10 bg-white/40 rounded-full"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-white/60"></span>
                <span className="h-px w-10 bg-white/40 rounded-full"></span>
              </div>
              <p className="text-white/70 text-sm mt-4 max-w-xl mx-auto">
                A simple, scalable, and proven model — built to bring quality healthcare to every corner of India.
              </p>
            </div>
            {/* Steps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {businessModelSteps.map((item, i) => (
                <div key={i} className="flex flex-col gap-3 p-5 bg-white/10 hover:bg-white/20 rounded-2xl border border-white/10 hover:border-white/30 transition-all duration-300 group">
                  <div className="text-secondary-300 font-heading font-extrabold text-2xl group-hover:scale-110 transition-transform duration-300">{item.step}</div>
                  <div className="font-semibold text-white text-base">{item.title}</div>
                  <div className="text-white/70 text-sm leading-relaxed">{item.desc}</div>
                </div>
              ))}
            </div>
            {/* CTA */}
            <div className="mt-8 flex justify-center">
              <Link to="/about" className="inline-flex items-center gap-2 bg-white text-primary-700 hover:bg-gray-50 font-semibold py-3 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                Learn More About Us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Store Locations */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              Our Locations
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">Find an</span>
              <span className="gradient-text">APlus Pharmacy</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">We have launched our first outlet in Salem, Tamil Nadu. More locations are coming soon as we grow!</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {outlets.map((outlet, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-gray-900">{outlet.area}</h3>
                    <p className="text-primary-600 text-sm font-medium">{outlet.city}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${outlet.status === 'Open 24/7' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {outlet.status}
                  </span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{outlet.address}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <a href={`tel:${outlet.phone}`} className="text-gray-600 text-sm hover:text-primary-600 transition-colors">{outlet.phone}</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">{outlet.hours}</span>
                  </li>
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-gray-500 mb-4">More outlets coming soon — Salem is just the beginning! 🚀</p>
            <Link to="/contact" className="btn-primary">Contact Us to Know More <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Coverage Map */}
      <CoverageMap
        badge="Where We Stand Today"
        title="One District Up,"
        titleHighlight="A Whole State to Go"
        subtitle="Every white district on this map is a doorstep we haven't reached yet. Salem is where APlus began — and where our next 100 outlets are headed."
        legendActive="Salem — our first outlet"
        legendOpen="Open for expansion"
        attribution=""
      />

      {/* Customer Reviews */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block"></span>
              Customer Stories
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">What Our</span>
              <span className="gradient-text">Customers Say</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-amber-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">Real feedback from APlus Pharmacy customers across Tamil Nadu.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Rajesh Kumar', location: 'Chennai', review: 'APlus Pharmacy saved me almost 70% on my monthly medicines. The staff is very helpful and the quality is excellent. Highly recommend!', rating: 5 },
              { name: 'Priya Sundaram', location: 'Coimbatore', review: 'The pharmacist at APlus explained my prescription in detail and suggested a generic alternative that worked just as well at a fraction of the cost.', rating: 5 },
              { name: 'Anand Krishnan', location: 'Madurai', review: 'Clean, organized store with all the medicines I need. The digital billing and loyalty program is a great touch. This is the future of pharmacy!', rating: 5 },
            ].map((review, i) => (
              <div key={i} className="reveal card">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">"{review.review}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{review.name}</div>
                    <div className="text-gray-500 text-xs">{review.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-secondary-600 to-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-4">Own an APlus Pharmacy Outlet</h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">Join our growing network of franchise partners. Low investment, proven model, strong brand support.</p>
          <Link to="/franchise" className="bg-white text-primary-700 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg inline-flex items-center gap-2">
            Explore Franchise <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pharmacy;
